import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, CheckCircle2, FileText, Camera, ArrowRight, ShieldCheck, Clock } from "lucide-react";
import { Card, Btn, Badge, Spinner } from "@/components/ui-kit";
import api, { API } from "@/lib/api";

const DOC_SPECS = {
  doctor: [
    { key: "photo", label: "Profile photograph", hint: "A clear headshot", required: true, icon: Camera, accept: "image/*" },
    { key: "registration", label: "Medical Council registration", hint: "State Medical Council / NMC certificate", required: true, icon: FileText, accept: "image/*,application/pdf" },
    { key: "id_proof", label: "Government ID proof", hint: "Aadhaar / PAN / Passport", required: true, icon: FileText, accept: "image/*,application/pdf" },
    { key: "membership", label: "Professional membership proof", hint: "IMA / specialty association (optional)", required: false, icon: FileText, accept: "image/*,application/pdf" },
    { key: "insurance", label: "Indemnity / malpractice insurance", hint: "Active professional indemnity policy (optional)", required: false, icon: FileText, accept: "image/*,application/pdf" },
    { key: "degree", label: "Degree certificate", hint: "MBBS / MS / MD / DNB (optional)", required: false, icon: FileText, accept: "image/*,application/pdf" },
    { key: "address_proof", label: "Address proof", hint: "Optional", required: false, icon: FileText, accept: "image/*,application/pdf" },
  ],
  physiotherapist: [
    { key: "photo", label: "Profile photograph", hint: "A clear headshot", required: true, icon: Camera, accept: "image/*" },
    { key: "registration", label: "Council / Registration certificate", hint: "State physiotherapy council", required: true, icon: FileText, accept: "image/*,application/pdf" },
    { key: "membership", label: "Professional membership proof", hint: "IAP / association", required: true, icon: FileText, accept: "image/*,application/pdf" },
    { key: "insurance", label: "Indemnity / malpractice insurance", hint: "Active policy document — or upload a signed waiver below", required: true, icon: FileText, accept: "image/*,application/pdf", altKey: "insurance_waiver" },
    { key: "insurance_waiver", label: "Indemnity insurance waiver", hint: "Signed waiver — only if you don't have an indemnity policy", required: false, icon: FileText, accept: "image/*,application/pdf" },
    { key: "degree", label: "Degree certificate", hint: "BPT / MPT (optional)", required: false, icon: FileText, accept: "image/*,application/pdf" },
    { key: "id_proof", label: "Government ID proof", hint: "Aadhaar / PAN (optional)", required: false, icon: FileText, accept: "image/*,application/pdf" },
    { key: "address_proof", label: "Address proof", hint: "Optional", required: false, icon: FileText, accept: "image/*,application/pdf" },
  ],
};

function statusTone(s) {
  return s === "Verified" ? "emerald" : s === "Rejected" ? "rose" : s === "Review" ? "amber" : "slate";
}

function DocTile({ spec, doc, userId, onChange, waived }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const Icon = spec.icon;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", spec.key);
      fd.append("file", file);
      await api.post(`/users/${userId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${spec.label} uploaded`);
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setBusy(false);
    if (ref.current) ref.current.value = "";
  };

  return (
    <div className={`rounded-2xl border p-3.5 ${doc ? "border-teal-200 bg-teal-50/40" : "border-slate-200 bg-white"}`} data-testid={`pro-doc-tile-${spec.key}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 overflow-hidden ${doc ? "bg-teal-100 text-teal-700" : "bg-slate-100 text-slate-500"}`}>
          {doc && spec.key === "photo" && (doc.content_type || "").startsWith("image/")
            ? <img src={`${API}/documents/${doc.id}/file?auth=${localStorage.getItem("praniq_token")}`} alt={spec.label} className="w-full h-full object-cover" data-testid={`pro-doc-photo-preview`} />
            : doc ? <CheckCircle2 size={18} /> : <Icon size={17} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[13.5px] font-semibold text-slate-800">{spec.label}</span>
            {spec.required && !waived && <Badge tone="amber">Required</Badge>}
            {!doc && waived && <Badge tone="emerald">Waiver submitted</Badge>}
            {!doc && !waived && <Badge tone="slate">Pending</Badge>}
          </div>
          {doc ? (
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-[11.5px] text-slate-500 truncate max-w-[150px]">{doc.original_filename}</span>
              <Badge tone={statusTone(doc.verification_status)}>{doc.verification_status}</Badge>
            </div>
          ) : (
            <div className="text-[11.5px] text-slate-400 mt-0.5">{spec.hint}</div>
          )}
        </div>
      </div>
      <input ref={ref} type="file" accept={spec.accept} className="hidden" onChange={upload} data-testid={`pro-doc-input-${spec.key}`} />
      <Btn size="sm" className="w-full mt-2.5 justify-center" variant={doc ? "" : "primary"} onClick={() => ref.current?.click()} disabled={busy} data-testid={`pro-doc-upload-${spec.key}`}>
        {busy ? <Spinner className="w-3.5 h-3.5" /> : <><Upload size={13} /> {doc ? "Replace" : "Upload"}</>}
      </Btn>
    </div>
  );
}

export default function ProDocuments({ userId, credentialStatus, onDone, subtitle, role }) {
  const DOC_SPEC = DOC_SPECS[role] || DOC_SPECS.physiotherapist;
  const [docs, setDocs] = useState(null);
  const load = useCallback(() => api.get(`/users/${userId}/documents`).then((r) => setDocs(r.data)), [userId]);
  useEffect(() => { load(); }, [load]);

  if (!docs) return <div className="flex justify-center py-16"><Spinner className="w-6 h-6 text-teal-600" /></div>;

  const byType = {};
  docs.forEach((d) => { if (!byType[d.doc_type]) byType[d.doc_type] = d; });
  const uploaded = DOC_SPEC.filter((s) => byType[s.key]).length;

  return (
    <div className="space-y-4" data-testid="pro-onboarding">
      <div className="text-center">
        <div className="inline-flex w-12 h-12 rounded-2xl bg-teal-50 text-teal-600 items-center justify-center mb-2"><ShieldCheck size={24} /></div>
        <h1 className="font-display text-xl font-bold text-slate-900">Complete your credentials</h1>
        <p className="text-[13px] text-slate-500 mt-1 px-2">{subtitle || "Upload your documents so PranIQ Admin can verify you. You can only be matched to cases once verified."}</p>
      </div>

      {credentialStatus === "Verified" ? (
        <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 rounded-xl px-3 py-2.5 text-[12.5px] font-medium" data-testid="pro-verify-status">
          <CheckCircle2 size={15} /> PranIQ verified — your credentials are approved.
        </div>
      ) : (
        <div className="flex items-center gap-2 bg-amber-50 text-amber-700 rounded-xl px-3 py-2.5 text-[12.5px] font-medium" data-testid="pro-verify-status">
          <Clock size={15} /> Documents pending verification by PranIQ.
        </div>
      )}

      <Card className="p-3.5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[12.5px] font-semibold text-slate-700">Documents</span>
          <span className="text-[12px] text-slate-500" data-testid="pro-doc-progress">{uploaded}/{DOC_SPEC.length} uploaded</span>
        </div>
        <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden mb-3">
          <div className="h-full bg-teal-600 transition-all" style={{ width: `${(uploaded / DOC_SPEC.length) * 100}%` }} />
        </div>
        <div className="grid grid-cols-1 gap-2.5">
          {DOC_SPEC.map((s) => <DocTile key={s.key} spec={s} doc={byType[s.key]} userId={userId} onChange={load} waived={!!(s.altKey && byType[s.altKey])} />)}
        </div>
      </Card>

      {onDone && (
        <Btn variant="primary" className="w-full justify-center py-3" onClick={onDone} data-testid="pro-onboarding-continue">
          Continue to app <ArrowRight size={16} />
        </Btn>
      )}
    </div>
  );
}
